/**
 * Phase 7.1 — RPC retry prompt.
 *
 * High-stakes user-initiated mutations (creating a research topic,
 * saving triage notes, etc.) sometimes fail because of a transient
 * network blip rather than a real validation problem. Today most
 * surfaces show a small inline red message and stop there — the user
 * has to manually re-do their action, often re-typing input. This
 * primitive surfaces the same failure as a Radix Dialog with a
 * "Tentar novamente" button that re-invokes the original mutation
 * with the original arguments. Cancel dismisses without retrying.
 *
 * The Dialog is held in a Zustand store so any surface can call
 * `useRetryPromptStore.getState().show({ ... })` from anywhere — no
 * prop drilling. `<RetryPromptHost>` (mounted once in App.tsx) is
 * what actually renders the dialog when the store has a pending
 * prompt.
 *
 * Wire-up template (in a mutation consumer):
 *
 *   try {
 *     await mutateAsync(input);
 *   } catch (err) {
 *     useRetryPromptStore.getState().show({
 *       title: "Falha ao criar tópico",
 *       description: "Verifique sua conexão e tente novamente.",
 *       errorMessage: err instanceof Error ? err.message : undefined,
 *       onRetry: () => mutateAsync(input),
 *     });
 *   }
 *
 * `onRetry` returns a Promise — the dialog awaits it, shows a
 * loading spinner while pending, and dismisses on success. If the
 * retry itself rejects, the dialog stays open with the new error
 * surfaced so the user can try again or cancel.
 */

import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { create } from "zustand";

export interface RetryPromptInput {
  /** Heading shown in the dialog. e.g. "Falha ao criar tópico". */
  title: string;
  /** Body copy explaining the failure. e.g. "Verifique sua conexão." */
  description: string;
  /** Optional verbatim error message — rendered in a code block under
   *  the description for diagnostics. */
  errorMessage?: string;
  /** The original mutation, re-invoked when the user clicks
   *  "Tentar novamente". Promise — the dialog awaits it. */
  onRetry: () => Promise<unknown>;
}

interface RetryPromptState {
  current: RetryPromptInput | null;
  show: (prompt: RetryPromptInput) => void;
  dismiss: () => void;
}

export const useRetryPromptStore = create<RetryPromptState>((set) => ({
  current: null,
  show: (prompt) => set({ current: prompt }),
  dismiss: () => set({ current: null }),
}));

export function RetryPromptHost() {
  const current = useRetryPromptStore((s) => s.current);
  const dismiss = useRetryPromptStore((s) => s.dismiss);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  // Reset transient state on every prompt change — clears the
  // pending-retry spinner and stale error message both when the
  // prompt is dismissed and when a new prompt opens over an old one.
  useEffect(() => {
    setRetrying(false);
    setRetryError(null);
  }, [current]);

  async function handleRetry() {
    if (!current) return;
    setRetrying(true);
    setRetryError(null);
    try {
      await current.onRetry();
      dismiss();
    } catch (err) {
      setRetryError(err instanceof Error ? err.message : "Tente novamente");
    } finally {
      setRetrying(false);
    }
  }

  return (
    <Dialog.Root
      open={current !== null}
      onOpenChange={(next) => {
        if (!next && !retrying) dismiss();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm dark:bg-slate-950/60" />
        <Dialog.Content
          data-testid="retry-prompt"
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
        >
          <VisuallyHidden>
            <Dialog.Description>
              {current?.description ?? "Falha temporária"}
            </Dialog.Description>
          </VisuallyHidden>

          <div className="flex items-start gap-3 px-5 py-4">
            <AlertTriangle
              size={20}
              className="mt-0.5 shrink-0 text-amber-500"
              aria-hidden="true"
            />
            <div className="flex-1">
              <Dialog.Title className="text-sm font-medium text-slate-700 dark:text-slate-200">
                {current?.title ?? "Falha"}
              </Dialog.Title>
              <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                {current?.description}
              </p>
              {(retryError ?? current?.errorMessage) && (
                <pre className="mt-2 overflow-x-auto rounded bg-slate-100 px-2 py-1 font-mono text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                  {retryError ?? current?.errorMessage}
                </pre>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-3 dark:border-slate-800">
            <button
              type="button"
              onClick={dismiss}
              disabled={retrying}
              className="rounded-md px-3 py-1.5 text-xs text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-50 dark:text-slate-400 dark:hover:bg-slate-800"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-700 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-slate-600 disabled:opacity-50 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-slate-300"
            >
              {retrying ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} />
              )}
              Tentar novamente
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
